/**
 * Activity licences, on the company page.
 *
 * What this answers that the register cannot: EMTAK is what a company SAYS it
 * does, and a licence is what the state has PERMITTED it to do. Two firms with
 * the same activity code are different prospects when one holds a building
 * permit and the other does not.
 *
 * The card is absent when the company holds no licence at all, for the same
 * reason the arrears card is: `licences` is empty both for a company with no
 * permits and for the whole mirror while the MTR import has never run, and a
 * card saying "no licences" cannot tell those apart.
 *
 * MTR's own status word is printed untranslated ("kehtiv", "peatatud"). An
 * operator cross-checking a licence against mtr.ttja.ee has to find the same
 * word on both screens, and a translated status is a word they then have to
 * translate back.
 */
import { useMemo, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { StatusPill } from "../../ui";
import type { ArLicence } from "../../../lib/ariregister";
import { Card, fmtDate } from "./detailCard";

type T = (key: string, vars?: Record<string, string | number>) => string;

/** How many rows to show before the "show all" button. A bus company holds 693
 *  of these, and the card is not the place to scroll through them. */
const PREVIEW = 8;

/** Whether MTR's status and MTR's own end date agree. They disagree on 4% of
 *  the file, and a permit whose end date has passed is not one the company can
 *  be told it holds. */
function isExpired(l: ArLicence): boolean {
  return !!l.validUntil && new Date(l.validUntil).getTime() < Date.now();
}

function LicenceRow({ l, t }: { l: ArLicence; t: T }) {
  const expired = isExpired(l);
  const valid = l.status === "kehtiv" && !expired;
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-border/60 py-2 last:border-0">
      <StatusPill tone={valid ? "ok" : expired ? "lt" : "wt"}>{l.status}</StatusPill>
      <span className="text-sm font-medium">{l.activity || l.area || l.kind}</span>
      {l.area && l.activity ? <span className="text-xs text-muted-foreground">· {l.area}</span> : null}
      <span className="crm-tnum ml-auto text-[11px] text-muted-foreground">
        {l.validFrom ? fmtDate(l.validFrom) : "-"}
        {" - "}
        {l.validUntil ? fmtDate(l.validUntil) : t("ariregister.licence.noEnd")}
      </span>
      {l.url ? (
        <a href={l.url} target="_blank" rel="noreferrer" className="text-[11px] underline">
          {l.number}
        </a>
      ) : (
        <span className="crm-tnum text-[11px] text-muted-foreground">{l.number}</span>
      )}
      {expired && l.status === "kehtiv" ? (
        <span className="w-full text-[11px] text-destructive">{t("ariregister.licence.expired")}</span>
      ) : null}
    </li>
  );
}

export function LicenceCard({ licences, t }: { licences: ArLicence[]; t: T }) {
  const [showAll, setShowAll] = useState(false);

  // Valid ones first, then by start date. The permits the company actually
  // holds have to be at the top of a list that can be 693 rows long.
  const sorted = useMemo(() => {
    return [...licences].sort((a, b) => {
      const av = a.status === "kehtiv" && !isExpired(a) ? 0 : 1;
      const bv = b.status === "kehtiv" && !isExpired(b) ? 0 : 1;
      if (av !== bv) return av - bv;
      return (b.validFrom ?? "").localeCompare(a.validFrom ?? "");
    });
  }, [licences]);

  if (sorted.length === 0) return null;

  const validCount = sorted.filter((l) => l.status === "kehtiv" && !isExpired(l)).length;
  const shown = showAll ? sorted : sorted.slice(0, PREVIEW);

  return (
    <Card
      title={t("ariregister.licence.title")}
      icon={BadgeCheck}
      aside={
        <span className="text-[11px] text-muted-foreground">
          {t("ariregister.licence.count", { n: validCount })}
        </span>
      }
    >
      <ul className="mb-2">
        {shown.map((l) => (
          <LicenceRow key={l.id} l={l} t={t} />
        ))}
      </ul>
      {sorted.length > PREVIEW ? (
        <button
          type="button"
          className="mt-3 text-xs text-primary hover:underline"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? t("ariregister.licence.showLess")
            : t("ariregister.licence.showAll", { n: sorted.length - PREVIEW })}
        </button>
      ) : null}
      <p className="mt-2 text-[11px] text-muted-foreground">{t("ariregister.licence.hint")}</p>
    </Card>
  );
}
